import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bot, 
  Zap, 
  Calendar, 
  FileText, 
  Users, 
  MessageSquare, 
  CheckSquare, 
  BarChart3,
  Search,
  Clock,
  Target,
  Lightbulb,
  ArrowRight,
  Command
} from 'lucide-react';

interface AgentCommand {
  id: string;
  command: string;
  description: string;
  icon: React.ComponentType<any>;
  category: 'project' | 'communication' | 'analysis' | 'automation';
  agent: string;
  parameters?: string[];
  examples?: string[];
}

interface AgentCommandPaletteProps {
  isVisible: boolean;
  onClose: () => void;
  onExecuteCommand: (command: AgentCommand, parameters?: string[]) => void;
  inputValue: string;
  onInputChange: (value: string) => void;
}

const AGENT_COMMANDS: AgentCommand[] = [
  {
    id: 'assign-task',
    command: '/assign',
    description: 'Assign a task to a team member with context and priority',
    icon: Users,
    category: 'project',
    agent: 'ProjectBot',
    parameters: ['assignee', 'task', 'priority'],
    examples: ['/assign @sarah "Review API documentation" high']
  },
  {
    id: 'summarize-conversation',
    command: '/summarize',
    description: 'Generate an AI summary of the current conversation or channel',
    icon: FileText,
    category: 'analysis',
    agent: 'SummaryBot',
    parameters: ['scope', 'format'],
    examples: ['/summarize last-hour bullet-points', '/summarize full-channel executive']
  },
  {
    id: 'create-task',
    command: '/create-task',
    description: 'Create a new project task with automatic context extraction',
    icon: CheckSquare,
    category: 'project',
    agent: 'TaskBot',
    parameters: ['title', 'description', 'due-date'],
    examples: ['/create-task "Fix login bug" "Users cannot authenticate" tomorrow']
  },
  {
    id: 'schedule-meeting',
    command: '/schedule',
    description: 'Schedule a meeting with automatic calendar integration',
    icon: Calendar,
    category: 'communication',
    agent: 'ScheduleBot',
    parameters: ['title', 'participants', 'duration'],
    examples: ['/schedule "Sprint Planning" @team 2h']
  },
  {
    id: 'analyze-metrics',
    command: '/analyze',
    description: 'Analyze project metrics and generate insights',
    icon: BarChart3,
    category: 'analysis',
    agent: 'MetricsBot',
    parameters: ['metric-type', 'time-range'],
    examples: ['/analyze velocity last-sprint', '/analyze burndown current-sprint']
  },
  {
    id: 'generate-report',
    command: '/report',
    description: 'Generate automated project status reports',
    icon: FileText,
    category: 'analysis',
    agent: 'ReportBot',
    parameters: ['report-type', 'recipients'],
    examples: ['/report status @stakeholders', '/report progress weekly']
  },
  {
    id: 'find-expert',
    command: '/expert',
    description: 'Find team members with specific expertise or knowledge',
    icon: Search,
    category: 'communication',
    agent: 'ExpertBot',
    parameters: ['skill', 'availability'],
    examples: ['/expert "React development" available-now']
  },
  {
    id: 'time-tracking',
    command: '/track',
    description: 'Start or stop time tracking for current task',
    icon: Clock,
    category: 'project',
    agent: 'TimeBot',
    parameters: ['action', 'task-id'],
    examples: ['/track start PROJ-123', '/track stop']
  },
  {
    id: 'set-reminder',
    command: '/remind',
    description: 'Set intelligent reminders with context awareness',
    icon: Target,
    category: 'automation',
    agent: 'ReminderBot',
    parameters: ['message', 'time', 'recipients'],
    examples: ['/remind "Review PR" tomorrow-9am @dev-team']
  },
  {
    id: 'brainstorm',
    command: '/brainstorm',
    description: 'Start an AI-assisted brainstorming session',
    icon: Lightbulb,
    category: 'communication',
    agent: 'IdeaBot',
    parameters: ['topic', 'participants'],
    examples: ['/brainstorm "Feature improvements" @product-team']
  },
  {
    id: 'create-schedule',
    command: '/create-schedule',
    description: 'Create a new automated task schedule with cron expression',
    icon: Calendar,
    category: 'automation',
    agent: 'SchedulerBot',
    parameters: ['task-name', 'cron-expression', 'bee-name'],
    examples: ['/create-schedule "Daily backup" "0 2 * * *" backupBee']
  },
  {
    id: 'book-meeting',
    command: '/book-meeting',
    description: 'Book a meeting with calendar integration and ARCOS workflow triggers',
    icon: Calendar,
    category: 'automation',
    agent: 'SchedulerBot',
    parameters: ['title', 'date', 'time', 'attendees'],
    examples: ['/book-meeting "Project Review" "2024-01-15" "14:00" "team@company.com"']
  },
  {
    id: 'list-bookings',
    command: '/list-bookings',
    description: 'List all upcoming bookings and meetings',
    icon: Clock,
    category: 'automation',
    agent: 'SchedulerBot',
    parameters: ['status', 'date-range'],
    examples: ['/list-bookings confirmed', '/list-bookings all "next-week"']
  },
  {
    id: 'list-schedules',
    command: '/list-schedules',
    description: 'List all active schedules and their next execution times',
    icon: Clock,
    category: 'automation',
    agent: 'SchedulerBot',
    parameters: ['status'],
    examples: ['/list-schedules active', '/list-schedules all']
  },
  {
    id: 'pause-schedule',
    command: '/pause-schedule',
    description: 'Pause or resume a scheduled task',
    icon: Target,
    category: 'automation',
    agent: 'SchedulerBot',
    parameters: ['schedule-id', 'action'],
    examples: ['/pause-schedule sched-001 pause', '/pause-schedule sched-001 resume']
  }
];

const AgentCommandPalette: React.FC<AgentCommandPaletteProps> = ({
  isVisible,
  onClose,
  onExecuteCommand,
  inputValue,
  onInputChange
}) => {
  const [filteredCommands, setFilteredCommands] = useState<AgentCommand[]>(AGENT_COMMANDS);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isVisible && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isVisible]);

  useEffect(() => {
    const filtered = AGENT_COMMANDS.filter(command => {
      const matchesSearch = command.command.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           command.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           command.agent.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
    setFilteredCommands(filtered);
    setSelectedIndex(0);
  }, [searchTerm]);

  useEffect(() => {
    if (inputValue.startsWith('/')) {
      const commandPart = inputValue.slice(1);
      setSearchTerm(commandPart);
    } else {
      setSearchTerm('');
    }
  }, [inputValue]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, filteredCommands.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredCommands[selectedIndex]) {
          handleCommandSelect(filteredCommands[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
    }
  };

  const handleCommandSelect = (command: AgentCommand) => {
    onInputChange(command.command + ' ');
    onExecuteCommand(command);
    onClose();
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'project': return 'text-slate-300 bg-slate-700/10 border-slate-700/20';
      case 'communication': return 'text-slate-300 bg-slate-700/10 border-slate-700/20';
      case 'analysis': return 'text-slate-300 bg-slate-700/10 border-slate-700/20';
      case 'automation': return 'text-slate-300 bg-slate-700/10 border-slate-700/20';
      default: return 'text-slate-400 bg-slate-700/10 border-slate-700/20';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'project': return CheckSquare;
      case 'communication': return MessageSquare;
      case 'analysis': return BarChart3;
      case 'automation': return Zap;
      default: return Bot;
    }
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="w-full max-w-2xl mx-4 geex-card border border-slate-700/30 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={handleKeyDown}
        >
          {/* Header */}
          <div className="p-4 border-b border-slate-700/30">
            <div className="flex items-center space-x-3 mb-3">
              <div className="p-2 rounded-lg bg-[#3439fa]/20">
                <Command size={20} className="text-[#3439fa]" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-200 text-shadow">Agent Command Palette</h2>
                <p className="text-xs text-slate-400">Execute agentic actions with slash commands</p>
              </div>
            </div>

            {/* Search Input */}
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <input
                ref={inputRef}
                type="text"
                placeholder="Type / to see commands or search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 geex-card border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#3439fa] focus:border-transparent text-sm"
              />
            </div>
          </div>

          {/* Commands List */}
          <div className="max-h-96 overflow-y-auto">
            {filteredCommands.length === 0 ? (
              <div className="p-8 text-center">
                <Bot size={48} className="mx-auto text-slate-400 mb-4" />
                <p className="text-slate-400">No commands found matching your search</p>
                <p className="text-xs text-slate-500 mt-2">Try searching for "assign", "summarize", or "create"</p>
              </div>
            ) : (
              <div className="p-2">
                {filteredCommands.map((command, index) => {
                  const IconComponent = command.icon;
                  const CategoryIcon = getCategoryIcon(command.category);
                  const isSelected = index === selectedIndex;

                  return (
                    <motion.div
                      key={command.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.02 }}
                      className={`p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                        isSelected 
                          ? 'bg-[#3439fa]/20 border border-[#3439fa]/30' 
                          : 'hover:bg-slate-800/50 border border-transparent'
                      }`}
                      onClick={() => handleCommandSelect(command)}
                    >
                      <div className="flex items-start space-x-3">
                        {/* Command Icon */}
                        <div className={`p-2 rounded-lg ${
                          isSelected ? 'bg-[#3439fa]/30' : 'bg-slate-700/50'
                        }`}>
                          <IconComponent size={16} className={
                            isSelected ? 'text-[#3439fa]' : 'text-slate-400'
                          } />
                        </div>

                        {/* Command Details */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className={`font-mono text-sm ${
                              isSelected ? 'text-[#3439fa]' : 'text-white'
                            }`}>
                              {command.command}
                            </span>
                            
                            {/* Category Badge */}
                            <div className={`flex items-center space-x-1 px-2 py-1 rounded-full border text-xs ${getCategoryColor(command.category)}`}>
                              <CategoryIcon size={10} />
                              <span className="capitalize">{command.category}</span>
                            </div>

                            {/* Agent Badge */}
                            <div className="flex items-center space-x-1 px-2 py-1 rounded-full bg-[#f82b8c]/10 border border-[#f82b8c]/20 text-xs">
                              <Bot size={10} className="text-[#f82b8c]" />
                              <span className="text-[#f82b8c]">{command.agent}</span>
                            </div>
                          </div>

                          <p className="text-sm text-slate-300 mb-2">{command.description}</p>

                          {/* Examples */}
                          {command.examples && command.examples.length > 0 && (
                            <div className="space-y-1">
                              {command.examples.slice(0, 2).map((example, exampleIndex) => (
                                <div key={exampleIndex} className="flex items-center space-x-2 text-xs">
                                  <ArrowRight size={10} className="text-slate-500" />
                                  <code className="text-slate-400 bg-slate-800/50 px-2 py-1 rounded">
                                    {example}
                                  </code>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Selection Indicator */}
                        {isSelected && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="w-2 h-2 bg-[#3439fa] rounded-full"
                          />
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-slate-700/30 bg-slate-900/50">
            <div className="flex items-center justify-between text-xs text-slate-400">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-1">
                  <kbd className="px-2 py-1 bg-slate-800 rounded text-xs">↑↓</kbd>
                  <span>Navigate</span>
                </div>
                <div className="flex items-center space-x-1">
                  <kbd className="px-2 py-1 bg-slate-800 rounded text-xs">Enter</kbd>
                  <span>Select</span>
                </div>
                <div className="flex items-center space-x-1">
                  <kbd className="px-2 py-1 bg-slate-800 rounded text-xs">Esc</kbd>
                  <span>Close</span>
                </div>
              </div>
              <div className="text-slate-500">
                {filteredCommands.length} command{filteredCommands.length !== 1 ? 's' : ''} available
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AgentCommandPalette;
